import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { addHours, getTime } from 'date-fns';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';

import { config } from '../../../config';
import { isAdmin, isDebt } from '../../shared/utils';
import { Debt, User } from '../data';
import { SessionService } from './session.service';
import { TokenResponse, TokenService } from './token.service';

interface LoginResponse extends TokenResponse {
  profile: Debt | User;
}

export interface Challenge {
  email: string;
  phone: string;
}

export interface ICustomerTokenDetails {
  first_name: string;
  last_name: string;
  amount: number;
  number: string;
  invoice_number: string;
  invoice_date: string;
  service_details: string;
}

interface ISessionTimeout {
  instances: {
    admin: ISessionTimeoutInstance;
    customer: ISessionTimeoutInstance;
  };
  key: string;
}

interface ISessionTimeoutInstance {
  loginRoute: string;
  timeInHours: number;
}

const sessionTimeout: ISessionTimeout = {
  instances: {
    admin: {
      loginRoute: 'admin/login',
      timeInHours: 4,
    },
    customer: {
      loginRoute: 'customers/login',
      timeInHours: 6,
    },
  },
  key: 'timeout',
};

@Injectable()
export class AuthService {
  profile: BehaviorSubject<Debt | User>;

  constructor(
    private http: HttpClient,
    private session: SessionService,
    private token: TokenService
  ) {
    this.profile = new BehaviorSubject(this.session.get('profile'));
    this.profile.subscribe((profile) => {
      if (profile && (isAdmin(profile) || isDebt(profile))) {
        this.handleSessionInvalidation();
      }
      this.session.set('profile', profile);
    });

    this.token.invalidTokenEmitter
      .switchMap(() => this.logout())
      .subscribe(() => window.location.reload());
  }

  // @TODO The timeout logic could potentially be moved to its own file
  /**
   * Set inital timeout or re-set existing timeout for
   * if setTimeout() has been lost by a browser refresh
   *
   * @return {void}
   */
  handleSessionInvalidation(): void {
    const {
      instances: { admin, customer },
      key,
    } = sessionTimeout;
    const instance = isAdmin(this.profile.value) ? admin : customer;

    try {
      let differenceInMilliseconds = null;
      let expiry = null;
      const now = new Date();
      const timeout = this.session.get(key);

      if (timeout.expiryTimestamp) {
        expiry = new Date(parseInt(timeout.expiryTimestamp, 10));
        differenceInMilliseconds = getTime(expiry) - getTime(now);

        if (differenceInMilliseconds <= 0) {
          this.logout().subscribe();
          return;
        } else if (timeout.id !== undefined) {
          clearTimeout(parseInt(timeout.id, 10));
        }

        timeout.id = this.queueSessionInvalidation(differenceInMilliseconds);
      } else {
        expiry = getTime(addHours(now, instance.timeInHours));
        differenceInMilliseconds = getTime(expiry) - getTime(now);
        timeout.expiryTimestamp = expiry.toString();
        timeout.id = this.queueSessionInvalidation(differenceInMilliseconds);
      }

      this.session.set(key, timeout);
    } catch (e) {
      // If timeout doesn't exist, create it
      this.session.set(key, {});
      this.handleSessionInvalidation();
    }
  }

  /**
   * Set a timeout to execute forLogoutAndRedirect()
   * in provided amount of milliseconds
   *
   * @param milliseconds
   * @return {string}
   */
  queueSessionInvalidation(milliseconds: number): string {
    return setTimeout(() => {
      this.logout().subscribe();
    }, milliseconds).toString();
  }

  /**
   * Check if user can access a page.
   *
   * @param {string} page
   * @return {Observable<boolean>}
   */
  access(page: string): Observable<boolean> {
    return this.http
      .get<{ data: any }>(`${config.apiLocation}/admin/access?page=${page}`)
      .map((res) => res.data);
  }

  /**
   * Check if we are authenticated.
   *
   * @return {Observable<boolean>}
   */
  check(): Observable<boolean> {
    return this.token.isAuthorized();
  }

  can(scope: string): Observable<boolean> {
    return this.token
      .getClaims()
      .map((claims) => claims.scopes.includes(scope));
  }

  as(): Observable<any> {
    return this.http
      .get<{ data: any }>(`${config.apiLocation}/v1/as`)
      .map((res) => res.data);
  }

  /**
   * Login to the api via user.
   *
   * @param {string} email
   * @param {string} password
   * @param {boolean} remember
   * @return {Observable<void>}
   */
  login(email: string, password: string, remember: boolean): Observable<any> {
    return this.http
      .post<LoginResponse>(`${config.apiLocation}/auth/login`, {
        email,
        password,
        remember,
      })
      .map((res) => this.authenticate(res));
  }

  /**
   * Logout from the api.
   *
   * @return {Observable<void>}
   */
  logout(): Observable<void> {
    return this.http
      .post<void>(`${config.apiLocation}/auth/logout`, {})
      .map(() => {
        this.token.setAccessToken(undefined);
        // Clear session
        this.session.set(sessionTimeout.key, {});
        this.profile.next(undefined);
        this.session.clear();
      });
  }

  /**
   * Lookup a debt for id and authentication method.
   *
   * @param {string} debtNumber
   * @return {Observable<{ id: string; method: string; token: string }>}
   */
  debtLookup(debtNumber: string): Observable<{ id: string; method: string, token: string }> {
    return this.http.get<{ id: string; method: string ; token: string; }>(
      `${config.apiLocation}/auth/lookup/${debtNumber}`
    );
  }

  /**
   * Login to the api via two factor.
   *
   * @param {string} debtId
   * @param {string} code
   * @return {Observable<void>}
   */
  twoFactor(debtId: string, code: string): Observable<void> {
    return this.http
      .post<LoginResponse>(`${config.apiLocation}/auth/two-factor/${debtId}`, {
        code,
      })
      .map(this.authenticate);
  }

  /**
   * Resend two factor auth code.
   *
   * @param debtId
   * @return {Observable<void>}
   */
  resendTwoFactor(debtId: string) {
    return this.http.post(
      `${config.apiLocation}/auth/two-factor/${debtId}/resend`,
      {}
    );
  }

  /**
   * Login to the api via pin.
   *
   * @param {string} debtId
   * @param {string} pin
   * @return {Observable<void>}
   */
  pin(debtId: string, pin: string): Observable<void> {
    return this.http
      .post<LoginResponse>(`${config.apiLocation}/auth/pin`, { debtId, pin })
      .map(this.authenticate);
  }

  /**
   * Get a challenge to verify customer
   *
   * @param {string} debtId
   * @return {Observable<Challenge>}
   */
  challenge(debtId: string): Observable<Challenge> {
    return this.http.get<Challenge>(
      `${config.apiLocation}/auth/challenge/${debtId}`
    );
  }

  /**
   * Attempt to verify a challenge.
   *
   * @param {string} debtId
   * @param {string} pin
   * @return {Observable<void>}
   */
  verify(debtId: string, attempt: Challenge): Observable<void> {
    return this.http
      .post<LoginResponse>(
        `${config.apiLocation}/auth/challenge/${debtId}`,
        attempt
      )
      .map(this.authenticate);
  }

  /**
   * Get customer and debt details for token.
   *
   * @TODO Write customer details interface or use partials?
   * @TODO Fix interfaces
   *
   * @param {string} auth_token
   * @return {any>}
   */
  getCustomerDetailsForToken(
    auth_token: string
  ): Observable<ICustomerTokenDetails> {
    return this.http.get<ICustomerTokenDetails>(
      `${config.apiLocation}/auth/token?auth_token=${auth_token}`
    );
  }

  /**
   * Attempt to verify a challenge.
   *
   * @param {string} debtId
   * @param {string} pin
   * @return {Observable<void>}
   */
  verifyToken(auth_token: string): Observable<void> {
    return this.http
      .post<LoginResponse>(`${config.apiLocation}/auth/token`, { auth_token })
      .map(this.authenticate);
  }

  /**
   * Accept a direct login link.
   *
   * @param {string} token
   */
  accept(token: string): Observable<void> {
    return this.http
      .post<LoginResponse>(`${config.apiLocation}/auth/accept`, { token })
      .map(this.authenticate);
  }

  authenticate = ({ access_token, profile }: LoginResponse): void => {
    if (isAdmin(profile)) {
      this.session.set(sessionTimeout.key, {}); // Force reset timeout object
      this.handleSessionInvalidation();
    } else if (location.pathname.includes('admin')) {
      // User is not an admin, stop execution
      return;
    }

    this.profile.next(profile);
    this.token.setAccessToken(access_token);
  }

  refreshProfile(): Observable<any> {
    return this.http
      .get<{ data: Debt | User }>(`${config.apiLocation}/v1/me`)
      .map(({ data }) => this.profile.next(data));
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/auth.service.ts